import { useCallback, useMemo } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

export default function useSplitedTable({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds,
  tables,
  columnLables,
  tableName,
  filderedFieldName = ''
}) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey = type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id');
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, tableName);
  };

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = useCallback(
    value => {
      const changes = value[0];
      const fund = isMultiFunds
        ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data[tableName]))
        : JSON.parse(JSON.stringify(data));

      fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
        cell: changes.newCell
      });

      onChangeFund(tableName, fund, changes.newCell.fundId);
    },
    [data]
  );

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const columns = useMemo(() => {
    let _columns = [];
    if (!isMultiFunds) {
      _columns = isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 2
          }));
    } else {
      const _fundSize = Object.keys(funds).length;
      _columns =
        !isComparisonReport && _fundSize <= 3
          ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
              columnId: index + 1,
              width: contentWidth / (_fundSize + 1)
            }))
          : isComparisonReport
            ? [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.values(funds)
                  .map(item => [item, item.previous_fund])
                  .flat()
                  .map((_, index) => ({
                    columnId: index + 2,
                    width: 300
                  }))
              ]
            : [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.keys(funds).map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
              ];
    }
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, contentWidth, isComparisonReport]);

  const _tables = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      fundSize: Object.keys(funds).length,
      contentWidth,
      isComparisonReport
    });

    return Object.keys(columnLables).map(key => {
      let _rows = [];
      if (!isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: columnLables[key]?.name
              },
              ...(isComparisonReport
                ? [
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                    },
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                    }
                  ]
                : [
                    {
                      type: 'header',
                      text: ''
                    }
                  ])
            ]
          }
        ];
      } else {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: columnLables[key]?.name
              },
              ...(isComparisonReport
                ? Object.values(funds)
                    .map(item => [item, item.previous_fund])
                    .flat()
                    .map((fund, ind) => ({
                      type: 'header',
                      text: fund?.fund_name || '',
                      colspan: ind % 2 === 0 ? 2 : undefined
                    }))
                : Object.values(funds).map(value => ({
                    type: 'header',
                    text: value?.fund_name || ''
                  })))
            ]
          }
        ];
      }
      return [
        ..._rows,
        ...tables.map((value, index) => {
          let cells = [];

          if (isMultiFunds) {
            cells = [
              {
                type: 'header',
                text: value?.name || '',

                style: {
                  'justify-content': 'flex-start',
                  'white-space': 'inherit'
                }
              },
              ...(isComparisonReport
                ? Object.values(funds)
                    .map(item => [item, item.previous_fund])
                    .flat()
                    .map((item, j) => {
                      const ind = item.fund_data[tableName].findIndex(
                        value => value[filderedFieldName] === key
                      );
                      return getColumnByType({
                        type: schema.types[value.key].type,
                        options: schema.types[value.key].options,
                        key: value.key,
                        defaultValue: schema.defaultValues[value.key],
                        index: ind,
                        value: item.fund_data[tableName][ind],
                        required: requiredFields.includes(value.key),
                        tableName: tableName,
                        isStaticTemplate,
                        fundId: item.fund_id,
                        isSectionDisabled: isTableDisabledForFund(item.fund_id),
                        fundName: item.fund_name,
                        hasDrillDownFeature:
                          !!item.fund_data[tableName][ind].report_id &&
                          item.fund_data[tableName][ind]?.[value.key] !== null &&
                          schema.types[value.key].has_drill_down_feature,
                        reportId: item.fund_data[tableName][ind].report_id,
                        tableId: item.fund_data[tableName]?.[ind]?.id,
                        title: columnLables[key]?.name,
                        isMultiFunds,
                        isEsma: true,
                        fieldOrder: columnLables[key]?.order,
                        difference:
                          j % 2 === 1
                            ? funds[item.fund_id]?.difference?.[tableName]?.[ind]?.[value.key] || {}
                            : null,
                        nonEditable: isComparisonReport
                      });
                    })
                : Object.values(funds).map(item => {
                    const ind = funds[item.fund_id].fund_data[tableName].findIndex(
                      value => value[filderedFieldName] === key
                    );
                    return getColumnByType({
                      type: schema.types[value.key].type,
                      options: schema.types[value.key].options,
                      key: value.key,
                      defaultValue: schema.defaultValues[value.key],
                      index: ind,
                      value: funds[item.fund_id].fund_data[tableName][ind],
                      required: requiredFields.includes(value.key),
                      tableName: tableName,
                      isStaticTemplate,
                      fundId: item.fund_id,
                      isSectionDisabled: isTableDisabledForFund(item.fund_id),
                      fundName: item.fund_name,
                      hasDrillDownFeature:
                        !!item.fund_data[tableName][ind].report_id &&
                        item.fund_data[tableName][ind]?.[value.key] !== null &&
                        schema.types[value.key].has_drill_down_feature,
                      reportId: item.fund_data[tableName][ind].report_id,
                      tableId: item.fund_data[tableName]?.[ind]?.id,
                      title: columnLables[key]?.name,
                      isMultiFunds,
                      isEsma: true,
                      fieldOrder: columnLables[key]?.order,
                      nonEditable: isComparisonReport
                    });
                  }))
            ];
          } else {
            const ind = data.findIndex(item => item[filderedFieldName] === key);

            cells = [
              {
                type:
                  data[ind]?.report_id &&
                  data[ind]?.[value.key] !== null &&
                  schema.types[value.key]?.has_drill_down_feature
                    ? 'customHeader'
                    : 'header',
                text: value?.name || '',
                render: () => (
                  <CustomHeaderWithDrillDown
                    title={value?.name}
                    reportId={data[ind]?.report_id}
                    fundId={data[ind].fund_id}
                    columnName={key}
                    tableId={data[ind]?.id}
                    tableName={tableName}
                  />
                ),
                style: {
                  'justify-content': 'flex-start',
                  'white-space': 'inherit'
                }
              },
              ...(isComparisonReport
                ? [
                    getColumnByType({
                      type: schema.types[value.key].type,
                      options: schema.types[value.key].options,
                      key: value.key,
                      defaultValue: schema.defaultValues[value.key],
                      index: ind,
                      value: funds[currentDataKey].previous_fund.fund_data[tableName][ind],
                      required: requiredFields.includes(value.key),
                      tableName: tableName,
                      isStaticTemplate,
                      isMultiFunds,
                      fundId: funds[currentDataKey]?.previous_fund?.fund_id,
                      isSectionDisabled: isTableDisabledForFund(
                        funds[currentDataKey]?.previous_fund
                      ),
                      fundName: funds[currentDataKey]?.previous_fund?.fund_name,
                      fieldOrder: columnLables[key]?.order,
                      nonEditable: isComparisonReport
                    })
                  ]
                : []),
              getColumnByType({
                type: schema.types[value.key].type,
                options: schema.types[value.key].options,
                key: value.key,
                defaultValue: schema.defaultValues[value.key],
                index: ind,
                value: data[ind],
                required: requiredFields.includes(value.key),
                tableName: tableName,
                isStaticTemplate,
                isMultiFunds,
                isEsma: true,
                fundId: data[ind]?.fund_id,
                isSectionDisabled: isTableDisabledForFund(data[ind]?.fund_id),
                fundName: data[ind]?.fund_name,
                fieldOrder: columnLables[key]?.order,
                difference: isComparisonReport
                  ? funds[currentDataKey]?.difference?.[tableName]?.[ind]?.[value.key] || {}
                  : null,
                nonEditable: isComparisonReport
              })
            ];
          }
          const ind = data.findIndex(item => item[filderedFieldName] === key);

          // Each value column stacks its own value above its own error, so the
          // row must fit the tallest column — pairing the longest value with a
          // different column's longest error over-sizes it.
          const rows = isMultiFunds
            ? (isComparisonReport
                ? Object.values(funds)
                    .map(fund => [fund.previous_fund, fund])
                    .flat()
                : Object.values(funds)
              ).map(fund => {
                const fundInd = fund.fund_data[tableName].findIndex(
                  row => row[filderedFieldName] === key
                );
                return fund.fund_data[tableName][fundInd];
              })
            : isComparisonReport
              ? [funds[currentDataKey].previous_fund.fund_data[tableName][ind], data[ind]]
              : [data[ind]];

          const rowHeight = getRowHeight({
            label: columnLables[key]?.name,
            labelWidth,
            cells: getRowCells(rows, value.key, schema?.types?.[value.key]?.options, false),
            valueWidth
          });

          return {
            rowId: isComparisonReport && isMultiFunds ? index + 2 : index + 1,
            height: rowHeight,
            cells: [
              {
                type: 'header',
                text: `${value.order}`
              },
              ...cells
            ]
          };
        })
      ];
    });
  }, [data, isMultiFunds, funds, isComparisonReport]);

  return { tables: _tables, columns, onChange };
}
